import { useMemo, useState } from 'react';
import { NJHeading, NJInputSearch, NJLink, NJPagination, NJSelect, NJText } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { ProjectAccessAvatars } from './ProjectAccessAvatars';
import type { ProjectSummary } from './types';

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr-FR');
const month = (value: string) => value.split('-').reverse().join('/');
const time = (value: string) => {
  const [date, hour] = value.split(' ');
  const [day, mon, year] = date.split('/');
  return new Date(`${year}-${mon}-${day}T${hour}`).getTime();
};

export function CompletedProjectsTable({ projects, onOpen }: { projects: ProjectSummary[]; onOpen: (id: string) => void }) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const rows = useMemo(() => projects.filter((project) => normalize([
    project.name, ...project.communeNames, project.owner, ...project.members,
    project.updatedAt, month(project.contract.start), month(project.contract.end),
  ].join(' ')).includes(normalize(search.trim()))), [projects, search]);
  const [sortedIds, setSortedIds] = useState<string[] | null>(null);
  const ordered = useMemo(() => sortedIds
    ? [...rows].sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id)) : rows, [rows, sortedIds]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const columns = useMemo<ColDef<ProjectSummary>[]>(() => [
    { field: 'name', headerName: 'Nom du projet', flex: 2.7, minWidth: 250,
      cellRenderer: ({ data }: ICellRendererParams<ProjectSummary>) => data && <NJLink size="sm" variant="contextual"
        href={`/plan2cost/prototype/plan-navigation-v3?projets=mixte#project-${data.id}`}
        title="Continuer vers l’accueil V3 pour ouvrir ce projet"
        onClick={(event) => { event.preventDefault(); onOpen(data.id); }}>{data.name}</NJLink> },
    { headerName: 'Périmètre · villes', colId: 'communes', valueGetter: ({ data }) => data?.communeNames.join(', '), flex: 1.4 },
    { field: 'updatedAt', headerName: 'Dernière modification', flex: 1.8, sort: 'desc', comparator: (a: string, b: string) => time(a) - time(b) },
    { headerName: 'Début du contrat', colId: 'start', valueGetter: ({ data }) => data?.contract.start,
      valueFormatter: ({ value }: { value: string }) => month(value), flex: 1.1 },
    { headerName: 'Fin du contrat', colId: 'end', valueGetter: ({ data }) => data?.contract.end,
      valueFormatter: ({ value }: { value: string }) => month(value), flex: 1.1 },
    { headerName: 'Personnes ayant accès', colId: 'people', flex: 1.9, minWidth: 190,
      valueGetter: ({ data }) => data ? [...new Set([data.owner, ...data.members])].join(', ') : '',
      cellRenderer: ({ data }: ICellRendererParams<ProjectSummary>) => data && <ProjectAccessAvatars project={data} /> },
  ], [onOpen]);
  return (
    <section className="pcv3-completed" aria-labelledby="pav-completed-title">
      <div className="pcv3-section-heading">
        <NJHeading as="h2" scale="md" id="pav-completed-title" aria-live="polite">Projets terminés ({rows.length})</NJHeading>
        <div className="pcv3-project-search" onClick={(event) => {
          if (event.target instanceof Element && event.target.closest('button')) { setSearch(''); setPage(1); }
        }}>
          <NJInputSearch label="Rechercher dans les projets terminés" aria-label="Rechercher dans les projets terminés"
            placeholder="Nom, commune, personne…" value={search} resetButtonTitle="Effacer la recherche"
            onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
        </div>
      </div>
      <div className="pav-grid">
        <DataTable<ProjectSummary> testId="completed-projects-table" header={columns}
          data={ordered.slice(start, start + pageSize)}
          defaultColDef={{ flex: 1, minWidth: 120, sortable: true, resizable: true, wrapHeaderText: true }}
          onGridReady={({ api }) => {
            api.setGridOption('rowHeight', 56);
            api.setGridOption('headerHeight', 48);
            api.setGridOption('overlayNoRowsTemplate', `<span>${projects.length ? 'Aucun projet ne correspond à la recherche.' : 'Aucun projet terminé.'}</span>`);
            // Sorting stays global before slicing, including when a larger mock is supplied.
            api.addEventListener('sortChanged', () => {
              const sort = api.getColumnState().find((column) => column.sort);
              if (!sort) { setSortedIds(null); return; }
              const key = sort.colId;
              const value = (p: ProjectSummary) => key === 'communes' ? p.communeNames.join(', ')
                : key === 'start' ? p.contract.start : key === 'end' ? p.contract.end
                  : key === 'people' ? [p.owner, ...p.members].join(', ') : key === 'updatedAt' ? p.updatedAt : p.name;
              setSortedIds([...projects].sort((a, b) => {
                const comparison = key === 'updatedAt' ? time(a.updatedAt) - time(b.updatedAt)
                  : value(a).localeCompare(value(b), 'fr-FR', { numeric: true });
                return sort.sort === 'desc' ? -comparison : comparison;
              }).map((p) => p.id));
              setPage(1);
            });
          }} />
      </div>
      <div className="pcv3-table-pagination">
        <div className="pcv3-page-size">
          <NJSelect.Root id="pav-page-size" label="Lignes par page" labelKind="static"
            listNavigationLabel="Utilisez les flèches puis Entrée pour choisir."
            buttonDefaultValueLabel="Choisir le nombre de lignes" initialValue="50"
            onChange={(value) => { if (value) { setPageSize(Number(value)); setPage(1); } }}>
            {[20, 50, 100, 500, 1000].map((size) => <NJSelect.Item key={size} value={String(size)} label={String(size)}>{String(size)}</NJSelect.Item>)}
          </NJSelect.Root>
        </div>
        <NJText scale="sm" role="status">{rows.length ? start + 1 : 0}–{Math.min(start + pageSize, rows.length)} sur {rows.length} projet{rows.length !== 1 ? 's' : ''}</NJText>
        <NJPagination key={pageCount} count={pageCount} currentItem={currentPage}
          ariaLabelPrevious="Page précédente des projets terminés" ariaLabelNext="Page suivante des projets terminés"
          onChange={setPage} />
      </div>
    </section>
  );
}
